<template>
  <EquipmentBase
    v-if="isSizingEspOnShore"
    :section-id="ScenarioDetailSectionKey.INTAKE"
    :class="{ 'compact-mode': !!stack }"
  >
    <!-- Compact Mode Layout -->
    <template v-if="stack">
      <!-- Header Row: Intake Title & Gas Curve Toggle -->
      <div class="compact-header-row" style="margin-top: 0.75rem">
        <div class="flex align-items-center">
          <h3 class="compact-title">Intake</h3>
          <CalculatorToggle
            :section-ids="[EquipmentCalculatorSectionKey.INTAKE_CONDITIONS]"
          />
        </div>
        <div class="flex align-items-center">
          <LabelledToggle v-model="showGasCurve" text="Gas Curve" />
        </div>
      </div>

      <!-- Gas Curve Chart - Right below header -->
      <transition name="vertical-slide">
        <GasCurveChart v-if="showGasCurve" draw-legend enable-mouse-tracking />
      </transition>

      <!-- Stats Row: Packer, GVR, Liquid Flow Rate -->
      <div class="compact-header-row">
        <div class="flex align-items-center gap-2 compact-header-controls">
          <Checkbox
            :input-id="`packer-checkbox-compact-${stack}`"
            :data-cy="`packer-checkbox-compact-${stack}`"
            :disabled="isScenarioReadonly || isImportedFromLegacy"
            binary
            :model-value="packerInstalled"
            @update:model-value="handlePackerToggle"
          />
          <label
            v-tooltip.top="'(All Gas Goes Through Pump)'"
            :for="`packer-checkbox-compact-${stack}`"
            class="compact-label"
          >
            Packer
          </label>
          <Divider layout="vertical" class="header-divider-compact" />
          <div class="flex align-items-center gap-1">
            <span class="compact-label">GVF at Pump:</span>
            <ConverterInput
              :model-value="gasEnteringPumpRatio"
              :base-unit="PercentageUnits.Ratio"
              :target-unit="PercentageUnits.Percentage"
              :model="PercentageUnits.options()"
              mode="read"
              class="compact-gvr-input"
            />
          </div>
          <Divider layout="vertical" class="header-divider-compact" />
          <div class="flex align-items-center gap-1">
            <span class="compact-label">Liquid Flow Rate:</span>
            <ConverterInput
              label=""
              :model-value="flowRateEnteringIntake"
              :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
              :target-unit="getLiquidFlowUnit()"
              :model="[]"
              :dropdown-disabled="true"
              :update-unit="false"
              :update-scenario="false"
              mode="convert"
              class="compact-flow-input"
            />
          </div>
        </div>
      </div>

      <Divider />

      <!-- Gas Separation Sections -->
      <div class="gas-separation-container flex flex-wrap gap-4">
        <!-- Natural Gas Separation -->
        <div class="compact-section flex-1 gas-separation-item">
          <div class="compact-section-header">Natural Gas Separation</div>
          <div class="compact-section-content">
            <div class="flex align-items-center gap-2 nowrap">
              <ConverterInput
                :class="{
                  'compact-override-input': true,
                  'custom-override': isNaturalGasOverridden,
                  'gas-separation-input': true
                }"
                :style="{ maxWidth: '280px', flex: '0 0 auto' }"
                :data-cy="`natural-gas-separation-input-${stack || 'full'}`"
                :label="
                  packerInstalled
                    ? 'Natural Gas Separation (Disabled)'
                    : isNaturalGasOverridden
                      ? 'Natural Gas Separation (Override)'
                      : 'Natural Gas Separation (Calculated)'
                "
                mode="write"
                :disabled="
                  packerInstalled || isScenarioReadonly || isImportedFromLegacy
                "
                :base-unit="PercentageUnits.Percentage"
                :model="PercentageUnits.options()"
                :model-value="currentNaturalGasSepValue"
                @update:model-value="handleNaturalGasValueChange"
              />
              <i
                v-if="isNaturalGasOverridden"
                v-tooltip.top="naturalGasOverrideTooltip"
                class="pi pi-eye override-flag-icon"
                aria-label="Override active"
              />
              <Button
                v-if="
                  isNaturalGasOverridden &&
                  !isScenarioReadonly &&
                  !isImportedFromLegacy
                "
                v-tooltip.top="'Reset to Calculated Value'"
                icon="pi pi-undo"
                class="p-button-text p-button-rounded p-button-sm"
                :disabled="packerInstalled"
                @click="resetNaturalGasToCalculated"
              />
            </div>
          </div>
        </div>

        <!-- Intake Gas Separation -->
        <div class="compact-section flex-1 gas-separation-item">
          <div class="compact-section-header">Intake Gas Separation</div>
          <div class="compact-section-content">
            <div class="flex align-items-center gap-2 nowrap">
              <div class="flex align-items-center gap-2">
                <ConverterInput
                  :class="{
                    'compact-override-input': true,
                    'custom-override': isIntakeGasOverridden,
                    'gas-separation-input': true
                  }"
                  :style="{ maxWidth: '280px', flex: '0 0 auto' }"
                  :label="
                    packerInstalled
                      ? 'Intake Separation (Disabled)'
                      : isIntakeGasOverridden
                        ? 'Intake Separation (Override)'
                        : 'Intake Separation (Calculated)'
                  "
                  mode="write"
                  :disabled="
                    packerInstalled ||
                    isScenarioReadonly ||
                    isImportedFromLegacy
                  "
                  :base-unit="PercentageUnits.Percentage"
                  :model="PercentageUnits.options()"
                  :model-value="currentIntakeGasSepValue"
                  @update:model-value="handleIntakeGasValueChange"
                />
                <i
                  v-if="isIntakeGasOverridden"
                  v-tooltip.top="intakeGasOverrideTooltip"
                  class="pi pi-eye override-flag-icon"
                  aria-label="Override active"
                />
                <Button
                  v-if="
                    isIntakeGasOverridden &&
                    !isScenarioReadonly &&
                    !isImportedFromLegacy
                  "
                  v-tooltip.top="'Reset to Calculated Value'"
                  icon="pi pi-undo"
                  class="p-button-text p-button-rounded p-button-sm"
                  :disabled="packerInstalled"
                  @click="resetIntakeGasToCalculated"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Full Mode Layout (default) -->
    <template v-else>
      <EquipmentHeader class="intake-header-full">
        <template #title>
          <div class="flex justify-content-between align-items-center">
            Intake
            <CalculatorToggle
              :section-ids="[EquipmentCalculatorSectionKey.INTAKE_CONDITIONS]"
            />
          </div>
        </template>
        <template #titleRight>
          <div class="flex align-items-center gap-2 flex-wrap">
            <div class="flex flex-column">
              <div class="flex align-items-center">
                <Checkbox
                  input-id="packer-checkbox"
                  data-cy="packer-checkbox"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  binary
                  :model-value="packerInstalled"
                  @update:model-value="handlePackerToggle"
                />
                <label for="packer-checkbox" class="body-2 mr-1">
                  &nbsp;Packer Installed
                </label>
              </div>
              <span
                class="body-1 ml-4"
                style="font-size: 0.875rem; color: var(--gray-600)"
              >
                (All Gas Goes Through Pump)
              </span>
            </div>
            <Divider layout="vertical" class="header-divider" />
            <ConverterInput
              label="Gas Volume Ratio at Pump"
              :model-value="gasEnteringPumpRatio"
              :base-unit="PercentageUnits.Ratio"
              :target-unit="PercentageUnits.Percentage"
              :model="PercentageUnits.options()"
              mode="read"
            />
            <Divider layout="vertical" class="header-divider" />
            <ConverterInput
              label="Liquid Flow Rate Entering Intake"
              :model-value="flowRateEnteringIntake"
              :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
              :target-unit="getLiquidFlowUnit()"
              :model="[]"
              :dropdown-disabled="true"
              :update-unit="false"
              :update-scenario="false"
              mode="convert"
            />
            <Divider layout="vertical" class="header-divider" />
            <LabelledToggle v-model="showGasCurve" text="Gas Curve" />
          </div>
        </template>
      </EquipmentHeader>

      <transition name="vertical-slide">
        <GasCurveChart v-if="showGasCurve" draw-legend enable-mouse-tracking />
      </transition>

      <Divider />
      <div class="flex flex-wrap gap-4">
        <GasNaturalSeparation
          :form="parametersForm"
          :stack="stack"
          class="flex-1"
          style="min-width: 300px"
        />
        <GasIntakeSeparation
          :form="parametersForm"
          :stack="stack"
          class="flex-1"
          style="min-width: 300px"
        />
      </div>
    </template>

    <br />
    <div v-if="intakesOrder?.length > 0" class="space-y-3">
      <IntakeRow
        v-for="(intakeXrefId, i) in intakesOrder"
        :key="i"
        :index="i"
        :intake="getIntake(intakeXrefId)"
        :hide-add-above="newIntakeExists"
        :hide-add-below="newIntakeExists"
        :hide-remove="false"
        :stack="stack"
        @add-above="addIntake(i)"
        @add-below="addIntake(i + 1)"
        @remove="removeIntake($event, i)"
        @update:intakes="updateIntake($event)"
      />
    </div>
    <div v-else class="mb-3">
      <IntakeRow
        :intake="generateIntakeXref()"
        :hide-add-above="true"
        :hide-add-below="true"
        :hide-remove="false"
        :stack="stack"
        @update:intakes="addIntake(0, $event)"
      />
    </div>
    <Divider />
    <!-- Shroud -->
    <EquipmentHeader class="intake-header-full">
      <template #title>
        <div class="flex justify-content-between align-items-center">
          Shroud
          <CalculatorToggle
            :section-ids="[EquipmentCalculatorSectionKey.INTAKE_CONDITIONS]"
          />
        </div>
      </template>
      <template #titleRight>
        <div class="flex align-items-center gap-2 flex-wrap"></div>
      </template>
    </EquipmentHeader>
    <div class="mb-3">
      <ShroudRow />
    </div>
  </EquipmentBase>
  <EquipmentBase v-else :section-id="ScenarioDetailSectionKey.INTAKE">
    <EquipmentHeader>
      <template #title>
        <div class="flex justify-content-between align-items-center">
          Suction
        </div>
      </template>
      <template #titleRight> </template>
    </EquipmentHeader>
    <Divider />
    <br />
    <div v-if="intakesOrder?.length > 0" class="space-y-3">
      <HpsIntakeRow
        v-for="(intakeXrefId, i) in intakesOrder"
        :key="i"
        :index="i"
        :intake="getIntake(intakeXrefId)"
        :hide-add-above="true"
        :hide-add-below="true"
        :hide-remove="false"
        @add-above="addIntake(i)"
        @add-below="addIntake(i + 1)"
        @remove="removeIntake($event, i)"
        @update:intakes="updateIntake($event)"
      />
    </div>
    <div v-else class="mb-3">
      <HpsIntakeRow
        :intake="generateIntakeXref()"
        :hide-add-above="true"
        :hide-add-below="true"
        :hide-remove="false"
        @update:intakes="addIntake(0, $event)"
      />
    </div>
  </EquipmentBase>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import ConverterInput from "@/components/ConverterInput.vue";
import { PercentageUnits } from "@/libs/units/percentage";
import { convert } from "@/libs/units";
import { LiquidFlowRateUnits } from "@/libs/units/flowRate";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import {
  getFieldName,
  getFieldValue
} from "@/composables/forms/stackAwareFields";
import { useGasSeparationOverride } from "@/composables/forms/useGasSeparationOverride";
import { usePackerWatcher } from "@/composables/forms/usePackerWatcher";
import Checkbox from "primevue/checkbox";
import Divider from "primevue/divider";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import IntakeRow from "@/components/equipment/intakes/IntakeRow.vue";
import ShroudRow from "@/components/equipment/shrouds/ShroudRow.vue";
import HpsIntakeRow from "@/components/equipment/hps_intakes/HpsIntakeRow.vue";
import LabelledToggle from "@/components/LabelledToggle.vue";
import { v4 } from "uuid";
import CalculatorToggle from "@/components/scenarios/calculator_sections/CalculatorToggle.vue";
import {
  equipmentFormKey,
  intakesFormKey,
  parametersFormKey,
  scenarioCalculationsKey,
  scenarioKey,
  sizingKey,
  sizingFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import GasCurveChart from "@/components/scenarios/charts/GasCurveChart.vue";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import GasNaturalSeparation from "@/components/scenarios/GasNaturalSeparation.vue";
import GasIntakeSeparation from "@/components/scenarios/GasIntakeSeparation.vue";
import type { XrefIntakes } from "@/composables/forms/formTypes";
import { Sizing_Pumping_Type_Enum } from "gql/api";
// import { useStackCalculations } from "@/composables/scenarioDetailProvider/useStackCalculations";
import type { StackLocation } from "@/components/equipment/d";

const props = defineProps<{
  stack?: StackLocation;
}>();

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const parametersForm = injectStrict(parametersFormKey);
const intakesForm = injectStrict(intakesFormKey);

// Use stack-specific calculations (access directly from stackCalculations.value, no wrapping)
// const stackCalculations = useStackCalculations(props.stack || "upper");
// const scenarioCalculations = computed(
//   () => stackCalculations.value as ScenarioCalculations
// );
const scenarioCalculations = injectStrict(scenarioCalculationsKey);
const { isScenarioReadonly, isImportedFromLegacy } = useEditPermissions();
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey);
const sizingForm = injectStrict(sizingFormKey, null);
const { getLiquidFlowUnit } = useScenarioUnits(scenario, sizing);

// Drive ESP vs HPS view from sizing type so HPS sizing always shows HPS content
const isSizingEspOnShore = computed(
  () =>
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
);
const showGasCurve = ref(false);
const gasEnteringPumpRatio = computed(
  () =>
    scenarioCalculations.fluidProperties
      .fluidPropertiesAfterAllGasSeparationAtPump.value.gasFlowVolumeRatio
);

// For compact mode - Natural Gas Separation
const percentCalcNaturalGasSep = computed(() => {
  return convert(
    scenarioCalculations.intakeCalculations.aggregate
      .calculatedNaturalGasSeparationEfficiencyRatio.value || 0,
    PercentageUnits.Ratio,
    PercentageUnits.Percentage
  );
});

// Field name helpers based on stack - type-safe
const packerField = computed(() =>
  getFieldName("packerInstalled", props.stack)
);

const packerInstalled = computed(
  () =>
    (getFieldValue(
      parametersForm.data.value,
      "packerInstalled",
      props.stack
    ) as boolean | undefined) ?? false
);

function handlePackerToggle(enabled: boolean) {
  const field = packerField.value;
  parametersForm.update({
    [field]: enabled
  } as Partial<typeof parametersForm.data.value>);
}

// Use composable for natural gas separation override
const naturalGasSeparation = useGasSeparationOverride(
  parametersForm,
  "naturalGasSepOverrideType",
  "naturalGasSepPercent",
  percentCalcNaturalGasSep,
  packerInstalled,
  props.stack
);

const isNaturalGasOverridden = naturalGasSeparation.isOverridden;
const currentNaturalGasSepValue = naturalGasSeparation.currentValue;
const naturalGasOverrideTooltip = naturalGasSeparation.overrideTooltip;
const handleNaturalGasValueChange = naturalGasSeparation.handleValueChange;
const resetNaturalGasToCalculated = naturalGasSeparation.resetToCalculated;

// For compact mode - Intake Gas Separation
const percentCalcIntakeGasSep = computed(() => {
  return convert(
    scenarioCalculations.intakeCalculations.aggregate
      .calculatedIntakeGasSeparationEfficiencyRatio.value || 0,
    PercentageUnits.Ratio,
    PercentageUnits.Percentage
  );
});

// Use composable for intake gas separation override
const intakeGasSeparation = useGasSeparationOverride(
  parametersForm,
  "intakeGasSepOverrideType",
  "intakeGasSepPercent",
  percentCalcIntakeGasSep,
  packerInstalled,
  props.stack
);

const isIntakeGasOverridden = intakeGasSeparation.isOverridden;
const currentIntakeGasSepValue = intakeGasSeparation.currentValue;
const intakeGasOverrideTooltip = intakeGasSeparation.overrideTooltip;
const handleIntakeGasValueChange = intakeGasSeparation.handleValueChange;
const resetIntakeGasToCalculated = intakeGasSeparation.resetToCalculated;

// Use composable for packer watcher (handles storing/restoring override state)
usePackerWatcher(
  parametersForm,
  packerInstalled,
  {
    naturalGasSeparation: {
      overrideTypeFieldKey: "naturalGasSepOverrideType",
      percentFieldKey: "naturalGasSepPercent",
      calculatedValue: percentCalcNaturalGasSep
    },
    intakeGasSeparation: {
      overrideTypeFieldKey: "intakeGasSepOverrideType",
      percentFieldKey: "intakeGasSepPercent",
      calculatedValue: percentCalcIntakeGasSep
    }
  },
  props.stack
);

const flowRateEnteringIntake = computed(
  () =>
    scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value
      ?.liquidFlowRateBBLD || 0
);

const intakesOrder = computed(() => {
  const orderKey =
    props.stack === "lower" ? "lower_intakes_order" : "intakes_order";
  const order = (equipment.value[orderKey] as string[] | undefined) || [];
  return order.filter((intakeId: string) => intakesForm.data.value[intakeId]);
});

const newIntakeExists = computed(() => {
  // Check if there are no intakes at all
  if (intakesOrder.value?.length === 0) {
    return true;
  }
  // Check if any intakes have null intake_id (indicating incomplete configuration)
  return intakesOrder.value.some((intakeId: string) => {
    const intake = intakesForm.data.value[intakeId];
    return intake && intake.intake_id === null;
  });
});

function generateIntakeXref(): XrefIntakes[number] {
  const isHps = sizing.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
  return {
    __typename: "scenario_equipment_xref_intakes",
    id: v4(),
    scenario_equipment_id: equipment.value.id,
    intake_id: null,
    winning_revision_id: null,
    winning_revision_parent_id: null,
    winning_revision_created_at: "now()",
    winning_revision_updated_at: "now()",
    winning_revision_deleted_at: null,
    use_high_strength_shaft: isHps
    // Note: r_intake is a read-only GraphQL join that gets resolved by the backend based on intake_id, so we omit it here
  } as XrefIntakes[number];
}

function addIntake(
  index: number,
  xrefIntake?: XrefIntakes[number]
): XrefIntakes[number] {
  const intake = xrefIntake || generateIntakeXref();

  const intakeIndex = index < 0 ? 0 : index;
  const orderKey =
    props.stack === "lower" ? "lower_intakes_order" : "intakes_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );
  newOrder.splice(intakeIndex, 0, intake.id);
  intakesForm.update({
    [intake.id]: intake
  });
  equipmentForm.update({
    [orderKey]: newOrder
  });
  return intake;
}

function removeIntake(intake: XrefIntakes[number], index: number): void {
  const orderKey =
    props.stack === "lower" ? "lower_intakes_order" : "intakes_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );
  newOrder.splice(index, 1);

  // xref exists in db, soft delete
  if (intakesForm.initialData.value?.[intake.id]) {
    intakesForm.update({
      [intake.id]: {
        ...intake,
        journal_deleted_at: "now()"
      }
    });
  }
  // xref was created but not saved, wipe out
  else {
    delete intakesForm.data.value[intake.id];
  }

  equipmentForm.update({
    [orderKey]: newOrder
  });
}

function updateIntake(intake: XrefIntakes[number]): void {
  intakesForm.update({
    [intake.id]: intake
  });
}

function getIntake(id: string): XrefIntakes[number] {
  return intakesForm.data.value[id];
}
</script>

<style lang="scss" scoped>
.intake-header-full {
  :deep(.equipment-header) {
    padding: 0.5rem 0 !important;
    gap: 1rem !important;
    margin-bottom: 0.5rem;
    grid-template-columns: minmax(20px, max-content) 1fr !important;

    .left {
      flex-shrink: 0;
    }

    .right {
      display: flex !important;
      flex: 1 1 auto;
      min-width: 0;
      justify-content: flex-start !important;
      align-items: center;
      gap: 1.5rem !important;
      flex-wrap: wrap;

      > * {
        flex: 0 0 auto;
      }

      .flex {
        gap: 1rem;
      }
    }
  }

  .header-divider {
    height: 2rem;
    margin: 0 0.5rem;
    opacity: 0.3;
  }
}

// Reduce padding for intake section
:deep(.equipment-row) {
  padding-top: 1em !important;
}
</style>
